import { ButtonSmallSubtleIcon, Icon } from "@studio3t/generic-components";
import styled from "styled-components";
import { JsonViewer } from "@/components/JsonViewer";
import { OverlayScroll } from "@/components/OverlayScroll";
import { SectionCard } from "@/components/SectionCard";
import { SkeletonBlock } from "@/components/Skeleton";
import { useScript } from "@/hooks/useApi";
import type { ResourceRef } from "@/types/api";
import { rhaiHighlightStyle, rhaiLanguage } from "../NewStudio/rhaiLanguage";

// Rhai language support for the read-only viewers — the Lezer parser's
// tree gives highlighting and folding alike.
const RHAI_LANGUAGE = rhaiLanguage;

// One script card (Figma 13421:49477), titled with the script's filename:
// card header with a copy action, the content in the shared Raw-configuration
// viewer (same CodeMirror chrome and right-click Copy menu) speaking Rhai.
export function ScriptCard({ script }: { script: ResourceRef }) {
  const { data, isLoading, error } = useScript(
    script.service_name,
    script.resource,
  );

  const content = data?.content ?? "";

  return (
    <SectionCard
      title={script.resource}
      divided
      actions={
        <ButtonSmallSubtleIcon
          icon={Icon.Copy}
          disabled={!content}
          onClick={() => navigator.clipboard.writeText(content).catch(() => {})}
          aria-label={`Copy ${script.resource}`}
        />
      }
    >
      {isLoading ? (
        <ScriptSkeleton aria-hidden="true">
          <SkeletonBlock $height="120px" />
        </ScriptSkeleton>
      ) : error ? (
        <ScriptError>
          Failed to load {script.service_name}/{script.resource}:{" "}
          {error instanceof Error ? error.message : "unknown error"}
        </ScriptError>
      ) : (
        <EditorZone>
          <JsonViewer
            value={content}
            language={RHAI_LANGUAGE}
            highlight={rhaiHighlightStyle}
          />
        </EditorZone>
      )}
    </SectionCard>
  );
}

/** One card per transform script the pipeline uses, in stage order. */
export function ScriptCards({ scripts }: { scripts: ResourceRef[] }) {
  // The index qualifies the key: a pipeline can stage the same script ref
  // more than once.
  return (
    <>
      {scripts.map((script, index) => (
        <ScriptCard
          key={`${script.service_name}/${script.resource}#${index}`}
          script={script}
        />
      ))}
    </>
  );
}

// The script cards' code zone, on the full Raw-configuration chrome (line
// numbers and fold chevrons), capped at the Editor/codeZone height
// (13421:50020). The editor grows to its content and the zone scrolls past
// the cap on the app's scrollbars (the Raw-configuration modal's recipe),
// not CodeMirror's native ones.
const EditorZone = styled(OverlayScroll)`
  max-height: 279px;
`;

const ScriptSkeleton = styled.div`
  padding: 14px;
`;

const ScriptError = styled.div`
  padding: 14px;
  font-size: 12px;
  color: ${(p) => p.theme.semanticTextLight};
`;
